import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { GanttChartSquare, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, EmptyState, Card } from "@/components/common";
import CpmGantt from "@/components/CpmGantt";

export default function Schedule() {
  const { companyParam, companyId } = useApp();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ["schedules", companyId],
    queryFn: async () => (await api.get("/schedules", { params: companyParam })).data,
  });

  useEffect(() => {
    if (schedules.length && !schedules.find((s) => s.id === selected)) setSelected(schedules[0].id);
  }, [schedules, selected]);

  const active = schedules.find((s) => s.id === selected);

  return (
    <div data-testid="schedule-page">
      <PageHeader title="Scheduling / CPM" subtitle="Critical Path Method schedules with forward/backward pass, float, and a starkly visible critical path for construction work." />
      {isLoading ? <div className="text-sm text-ink-muted">Loading…</div> : schedules.length === 0 ? (
        <EmptyState icon={GanttChartSquare} title="No schedules yet" hint="Submit a Schedule / CPM directive for a construction job to generate a baseline schedule." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-1 h-fit">
            <div className="px-4 py-3 border-b border-border text-[11px] uppercase tracking-wider font-semibold text-ink-muted">Schedules</div>
            <div className="divide-y divide-border">
              {schedules.map((s) => (
                <button key={s.id} onClick={() => setSelected(s.id)} data-testid={`schedule-item-${s.id}`} className={`w-full text-left px-4 py-3 transition-colors ${selected === s.id ? "bg-brand/5 border-l-2 border-brand" : "border-l-2 border-transparent hover:bg-surface-muted"}`}>
                  <div className="text-sm font-semibold text-ink leading-tight">{s.name}</div>
                  <div className="text-[11px] font-mono text-ink-faint mt-1">{s.project_duration}d · {s.critical_path.length} critical</div>
                </button>
              ))}
            </div>
          </Card>
          <div className="lg:col-span-3">
            {active && (
              <Card>
                <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                  <div className="flex items-center gap-2"><GanttChartSquare className="w-4 h-4 text-brand" /><h3 className="font-heading text-sm font-semibold uppercase tracking-tight">{active.name}</h3></div>
                  <button onClick={() => navigate(`/jobs/${active.job_id}`)} className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-brand font-semibold">View Job <ArrowRight className="w-3 h-3" /></button>
                </div>
                <div className="p-5"><CpmGantt schedule={active} /></div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
